import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactCycleApiService } from '../services/contact-cycle-api.service';
import { ContactCycleRequest, ContactCycleResponse } from '../models/contact-cycle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NAME_PATTERN, normalizeForComparison } from '../../../shared/utils/text-normalization.utils';

@Component({
  selector: 'app-contact-cycle-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-cycle-form.component.html'
})
export class ContactCycleFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  cycleId: number | null = null;

  // Form model. displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  name = '';
  private originalName = '';
  description = '';
  triggerAction: 'NONE' | 'CREATE_DEAL' | 'CREATE_LEAD' = 'NONE';
  isActive = true;

  private existingCycles: ContactCycleResponse[] = [];

  constructor(
    private cycleApi: ContactCycleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarCiclosExistentes();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.cycleId = +id;
      this.cargarCiclo(this.cycleId);
    }
  }

  private cargarCiclosExistentes(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.cycleApi.getCycles(businessId).subscribe({
      next: (data) => { this.existingCycles = data; },
      error: () => { /* validación se delega al backend si falla */ }
    });
  }

  private esNombreDuplicado(nombre: string): boolean {
    const target = normalizeForComparison(nombre);
    return this.existingCycles.some(c =>
      normalizeForComparison(c.name) === target && c.id !== this.cycleId
    );
  }

  private cargarCiclo(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.getCycleById(id, businessId).subscribe({
      next: (ciclo) => {
        this.name = ciclo.name;
        this.originalName = ciclo.name;
        this.description = ciclo.description || '';
        this.triggerAction = ciclo.triggerAction;
        this.isActive = ciclo.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el ciclo';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Ciclo' : 'Nuevo Ciclo';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    if (!NAME_PATTERN.test(this.name.trim())) {
      const msg = 'El nombre solo puede contener letras, espacios, apóstrofes y guiones';
      this.error = msg;
      this.toast.warning({ title: 'Nombre inválido', message: msg });
      return;
    }

    if (this.esNombreDuplicado(this.name)) {
      const msg = `Ya existe un ciclo con el nombre "${this.name.trim()}"`;
      this.error = msg;
      this.toast.warning({ title: 'Nombre duplicado', message: msg });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const request: ContactCycleRequest = {
      businessId,
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      triggerAction: this.triggerAction,
      isActive: this.isActive
    };

    if (this.isEditing && this.cycleId) {
      this.cycleApi.updateCycle(this.cycleId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Ciclo de contacto',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.cycleApi.createCycle(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Ciclo de contacto', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/ciclos']);
  }
}
